/**
 * Building blocks shared by the two-factor (allauth MFA) pages.
 *
 * The MFA pages are plain Django form posts like the other auth pages; these
 * components only keep their look consistent with the login and password pages.
 */
import { Alert, AlertColor, Box, Button, Link, TextField } from "@mui/material";
import type { ButtonProps } from "@mui/material";
import { ReactNode, useState } from "react";
import { BRAND_COLORS } from "./AuthLayout";
import { renderFieldErrors } from "./FieldErrorList";
import { useAuthContext, useNonFieldErrors } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

const BACK_TO_PROFILE = gettext("Back to profile settings");
const BACK_TO_TWO_FACTOR = gettext("Back to two-factor authentication");
const CODE_LABEL = gettext("Code");

const ALERT_SEVERITIES: AlertColor[] = ["success", "info", "warning", "error"];

/** allauth's MFA URLs (see nest/urls.py), plus the app page that links here. */
export const MFA_URLS = {
  index: "/accounts/2fa/",
  activateTotp: "/accounts/2fa/totp/activate/",
  deactivateTotp: "/accounts/2fa/totp/deactivate/",
  recoveryCodes: "/accounts/2fa/recovery-codes/",
  generateRecoveryCodes: "/accounts/2fa/recovery-codes/generate/",
  downloadRecoveryCodes: "/accounts/2fa/recovery-codes/download/",
  authenticate: "/accounts/2fa/authenticate/",
  logout: "/accounts/logout/",
  login: "/accounts/login/",
  profile: "/users/profile/?tab=password",
} as const;

/** Padded white body below the AuthLayout header, as on the other auth pages. */
export function MfaContent({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(0, 5, 3),
        backgroundColor: "white",
      })}
    >
      {children}
    </Box>
  );
}

export function MfaText({ children }: { children: ReactNode }) {
  return (
    <Box
      component="p"
      sx={(theme) => ({
        fontSize: "0.875rem",
        color: BRAND_COLORS.text.secondary,
        lineHeight: 1.6,
        margin: theme.spacing(0, 0, 2),
      })}
    >
      {children}
    </Box>
  );
}

/**
 * The Django messages allauth queued for this page (e.g. "Authenticator app
 * activated.") plus the form's non-field errors.
 */
export function MfaMessages() {
  const { messages = [] } = useAuthContext();
  const nonFieldErrors = useNonFieldErrors();
  const alerts = [
    ...messages.map((message) => ({
      severity: ALERT_SEVERITIES.includes(message.level as AlertColor)
        ? (message.level as AlertColor)
        : "info",
      text: message.message,
    })),
    ...nonFieldErrors.map((error) => ({
      severity: "error" as AlertColor,
      text: error,
    })),
  ];
  if (alerts.length === 0) {
    return null;
  }
  return (
    <Box data-testid="mfa-messages">
      {alerts.map((alert, index) => (
        <Alert
          key={index}
          severity={alert.severity}
          sx={(theme) => ({ marginBottom: theme.spacing(2) })}
        >
          {alert.text}
        </Alert>
      ))}
    </Box>
  );
}

interface PostFormProps {
  action: string;
  children: ReactNode;
  id?: string;
}

/** A form that posts to Django with the CSRF token, like the other auth pages. */
export function PostForm({ action, children, id }: PostFormProps) {
  const { csrfToken } = useAuthContext();
  return (
    <Box component="form" method="POST" action={action} id={id}>
      <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
      {children}
    </Box>
  );
}

const primaryButtonSx = {
  padding: "0.75rem",
  backgroundColor: BRAND_COLORS.primary,
  borderRadius: 2,
  fontSize: "0.9375rem",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Montserrat', sans-serif",
  "&:hover": {
    backgroundColor: BRAND_COLORS.primaryHover,
  },
} as const;

const secondaryButtonSx = {
  padding: "0.75rem",
  borderRadius: 2,
  fontSize: "0.9375rem",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Montserrat', sans-serif",
  color: BRAND_COLORS.primary,
  borderColor: BRAND_COLORS.inputBorder,
  "&:hover": {
    borderColor: BRAND_COLORS.primary,
    backgroundColor: "transparent",
  },
} as const;

export function PrimaryButton({ sx, ...props }: ButtonProps) {
  return (
    <Button
      variant="contained"
      fullWidth
      sx={[primaryButtonSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    />
  );
}

export function SecondaryButton({ sx, ...props }: ButtonProps) {
  return (
    <Button
      variant="outlined"
      fullWidth
      sx={[secondaryButtonSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    />
  );
}

export function DangerButton({ sx, ...props }: ButtonProps) {
  return (
    <Button
      variant="contained"
      color="error"
      fullWidth
      sx={[
        { ...primaryButtonSx, backgroundColor: BRAND_COLORS.error },
        { "&:hover": { backgroundColor: BRAND_COLORS.error, opacity: 0.9 } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    />
  );
}

interface CodeFieldProps {
  errors: string[];
  label?: string;
  helperText?: string;
  autoFocus?: boolean;
}

/** The one-time-code input; allauth names the field `code` on every MFA form. */
export function CodeField({
  errors,
  label = CODE_LABEL,
  helperText,
  autoFocus = true,
}: CodeFieldProps) {
  const [code, setCode] = useState("");
  return (
    <TextField
      fullWidth
      name="code"
      label={label}
      value={code}
      onChange={(e) => setCode(e.target.value)}
      error={errors.length > 0}
      helperText={errors.length > 0 ? renderFieldErrors(errors) : helperText}
      required
      autoFocus={autoFocus}
      sx={(theme) => ({
        marginBottom: theme.spacing(3),
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          "&.Mui-focused fieldset": {
            borderColor: BRAND_COLORS.inputFocus,
          },
        },
        "& .MuiInputLabel-root": {
          fontWeight: 500,
          color: BRAND_COLORS.text.primary,
        },
      })}
      slotProps={{
        htmlInput: {
          autoComplete: "one-time-code",
          inputMode: "numeric",
          spellCheck: false,
          "data-testid": "mfa-code-input",
        },
      }}
    />
  );
}

const footerLinkSx = {
  display: "block",
  textAlign: "center",
  marginTop: 3,
  color: BRAND_COLORS.link,
  fontSize: "0.875rem",
  fontWeight: 500,
  textDecoration: "none",
  "&:hover": {
    color: BRAND_COLORS.linkHover,
    textDecoration: "underline",
  },
} as const;

/** Link back to the 2FA overview (from a sub-page) or to the app (from the overview). */
export function MfaBackLink({ to }: { to: "index" | "profile" }) {
  return (
    <Link
      href={MFA_URLS[to]}
      sx={footerLinkSx}
      data-testid={`mfa-back-to-${to}`}
    >
      {to === "index" ? BACK_TO_TWO_FACTOR : BACK_TO_PROFILE}
    </Link>
  );
}
